import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaMapMarkerAlt, FaCar, FaCheckCircle, FaMoneyBillWave, FaArrowRight, FaShieldAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const MEULAN_COORDS = { lat: 49.0067, lon: 1.9100 };

const FeeCalculator = () => {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [distance, setDistance] = useState(null);
    const [fee, setFee] = useState(0);
    const [displayedFee, setDisplayedFee] = useState(0);

    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371;
        const dLat = (lat2 - lat1) * (Math.PI / 180);
        const dLon = (lon2 - lon1) * (Math.PI / 180);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    useEffect(() => {
        if (query.length > 5 && !selectedAddress) {
            const delayDebounceFn = setTimeout(() => {
                fetchAddresses();
            }, 300);
            return () => clearTimeout(delayDebounceFn);
        } else if (query.length <= 5) {
            setSuggestions([]);
        }
    }, [query, selectedAddress]);

    const fetchAddresses = async () => {
        try {
            const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5`);
            const data = await response.json();
            setSuggestions(data.features || []);
        } catch (error) {
            console.error("Error fetching addresses:", error);
        }
    };

    const handleSelectAddress = (feature) => {
        const [lon, lat] = feature.geometry.coordinates;
        const dist = calculateDistance(MEULAN_COORDS.lat, MEULAN_COORDS.lon, lat, lon);

        let calculatedFee = 0;
        if (dist > 20.5) {
            calculatedFee = (dist - 20) * 0.70;
        }

        setSelectedAddress(feature.properties.label);
        setDistance(dist.toFixed(1));
        setFee(calculatedFee.toFixed(2));
        setSuggestions([]);
        setQuery(feature.properties.label);

        // Counter Animation Logic
        let start = 0;
        const end = parseFloat(calculatedFee);
        const duration = 800;
        const stepTime = 16;
        const increments = end / (duration / stepTime);

        if (end > 0) {
            const timer = setInterval(() => {
                start += increments;
                if (start >= end) {
                    setDisplayedFee(end.toFixed(2));
                    clearInterval(timer);
                } else {
                    setDisplayedFee(start.toFixed(2));
                }
            }, stepTime);
        } else {
            setDisplayedFee(0);
        }

        localStorage.setItem('rp_diagnostic_address', feature.properties.label);
        localStorage.setItem('rp_diagnostic_distance', dist.toFixed(1));
        localStorage.setItem('rp_diagnostic_fee', calculatedFee.toFixed(2));
        localStorage.setItem('rp_diagnostic_coords', JSON.stringify({ lat, lon }));

        window.dispatchEvent(new CustomEvent('map-update', {
            detail: { lat, lon, distance: dist }
        }));
    };

    return (
        <section className="fee-calculator-wrapper">
            <motion.div
                className="calculator-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
            >
                <div className="calc-header-marketing">
                    <span className="calc-badge">Diagnostic Instantané</span>
                    <h2 className="calc-title-premium">
                        On vient à <span className="text-highlight-gold">votre rencontre</span>.
                    </h2>
                    <p className="calc-subtitle-premium">
                        Roulez Propre se déplace directement chez vous ou sur votre lieu de travail.
                        Vérifiez immédiatement votre éligibilité.
                    </p>
                </div>

                <div className="search-container">
                    <div className="address-input-wrapper">
                        <FaSearch className="search-icon-calc" />
                        <input
                            type="text"
                            className="address-input"
                            placeholder="Saisissez votre ville ou adresse exacte..."
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                if (selectedAddress) setSelectedAddress(null);
                            }}
                        />
                    </div>

                    <AnimatePresence>
                        {suggestions.length > 0 && (
                            <motion.ul
                                className="suggestions-list"
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                            >
                                {suggestions.map((s, i) => (
                                    <li
                                        key={i}
                                        className="suggestion-item"
                                        onClick={() => handleSelectAddress(s)}
                                    >
                                        <FaMapMarkerAlt style={{ color: '#3b82f6' }} />
                                        {s.properties.label}
                                    </li>
                                ))}
                            </motion.ul>
                        )}
                    </AnimatePresence>
                </div>

                <AnimatePresence>
                    {distance !== null && selectedAddress && (
                        <motion.div
                            className={`result-container ${fee == 0 ? 'result-success' : 'result-notice'}`}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                        >
                            <div className="result-layout-marketing">
                                <div className="result-icon-box">
                                    {fee == 0 ? <FaCheckCircle /> : <FaMoneyBillWave />}
                                </div>
                                <div className="result-text-content">
                                    <span className="result-title-huge">
                                        {fee == 0 ? "Déplacement OFFERT !" : "Service disponible"}
                                    </span>
                                    <p className="result-desc-marketing">
                                        {fee == 0
                                            ? `Bonne nouvelle ! Vous êtes à ${distance} km de notre base. Aucun frais de déplacement ne sera ajouté à votre facture.`
                                            : `Nous pouvons intervenir chez vous (${distance} km). Un léger supplément de déplacement s'applique.`
                                        }
                                    </p>

                                    <Link to="/contact" className="btn-reserve-premium">
                                        Réserver maintenant <FaArrowRight />
                                    </Link>
                                </div>

                                {fee > 0 && (
                                    <div className="result-price-box">
                                        <div className="calc-price-hero">
                                            {displayedFee}<span className="price-currency">€</span>
                                        </div>
                                        <span style={{ color: '#94a3b8', fontSize: '0.8rem', fontWeight: 600 }}>FRAIS DE PORT</span>
                                    </div>
                                )}
                            </div>
                            <div className="pulse-marketing"></div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div style={{ marginTop: '30px', display: 'flex', gap: '20px', opacity: 0.6, fontSize: '0.85rem' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <FaShieldAlt /> Paiement sécurisé
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <FaCheckCircle /> Service garanti
                    </span>
                </div>
            </motion.div>
        </section>
    );
};

export default FeeCalculator;
